import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import {
  formatDate,
  formatDuration,
  formatBytes,
  getSessionStatusColor,
  formatTraffic
} from '../../utils/format';

const SessionCard = ({ session, index }) => {
  const { t } = useTranslation();

  // Безопасное получение числового значения
  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  const getProtocolBadgeColor = (protocol) => {
    return protocol === 'tcp' ? 'blue' : 'purple';
  };

  // Варианты анимации для появления
  const cardVariants = {
    hidden: {
      opacity: 0,
      x: -20,
      height: 0,
      marginBottom: 0
    },
    visible: {
      opacity: 1,
      x: 0,
      height: 'auto',
      marginBottom: '1rem',
      transition: {
        duration: 0.3,
        ease: 'easeOut',
        delay: index * 0.03 // Каскадная задержка
      }
    },
    exit: {
      opacity: 0,
      x: 20,
      height: 0,
      marginBottom: 0,
      transition: {
        duration: 0.2,
        ease: 'easeIn'
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      layoutId={session.id}
      transition={{
        layout: { duration: 0.3, ease: 'easeInOut' }
      }}
    >
      <Card 
        className={`hover:shadow-lg transition-all duration-200 ${
          session.status === 'active' 
            ? 'border-l-4 border-l-green-500 hover:border-l-green-600' 
            : 'border-l-4 border-l-gray-400 opacity-80 hover:opacity-100'
        }`}
      >
        <motion.div 
          className="flex items-start justify-between"
          layout
        >
          <div className="flex-1">
            <motion.div 
              className="flex items-center space-x-3 mb-4"
              layout
            >
              <motion.div 
                className={`p-2 rounded-lg ${
                  session.status === 'active' 
                    ? 'bg-green-100' 
                    : 'bg-gray-100'
                }`}
                layout
                animate={{ 
                  backgroundColor: session.status === 'active' ? 'rgb(220 252 231)' : 'rgb(243 244 246)' 
                }}
                transition={{ duration: 0.3 }}
              >
                {session.status === 'active' ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </motion.div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('sessions.sessionCard.title')} {session.userSessionNumber || session.id}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant={getSessionStatusColor(session.status)}>
                    {t(`sessions.sessionCard.status.${session.status}`)}
                  </Badge>
                  <Badge variant={getProtocolBadgeColor(session.protocol)}>
                    {session.protocol?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm"
              layout
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                <span className="text-gray-600">{t('sessions.sessionCard.proxy')}</span>
                <span className="ml-2 font-medium text-gray-900">{session.proxyId}</span>
              </div>

              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span className="text-gray-600">{t('sessions.sessionCard.port')}</span>
                <span className="ml-2 font-medium text-gray-900">{session.port}</span>
              </div>

              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                </svg>
                <span className="text-gray-600">{t('sessions.sessionCard.client')}</span>
                <span className="ml-2 font-medium text-gray-900">{session.clientAddress || '-'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-600">{t('sessions.sessionCard.connected')}</span>
                <span className="ml-2 font-medium text-gray-900">
                  {formatDate(session.connectedAt)}
                </span>
              </div>

              {session.disconnectedAt && (
                <motion.div
                  className="flex items-center space-x-2"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-600">{t('sessions.sessionCard.disconnected')}</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {formatDate(session.disconnectedAt)}
                  </span>
                </motion.div>
              )}

              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600">{t('sessions.sessionCard.duration')}</span>
                <motion.span
                  className="ml-2 font-medium text-gray-900"
                  key={session.duration}
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {formatDuration(session.duration)}
                </motion.span>
              </div>
            </motion.div>

            {/* Traffic Information */}
            <motion.div
              className="mt-4 pt-4 border-t border-gray-200"
              layout
            >
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span className="text-gray-600">{t('sessions.sessionCard.traffic')}</span>
                  <motion.span
                    className="ml-2 font-medium text-gray-900"
                    key={`${session.bytesSent}-${session.bytesReceived}`}
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {formatTraffic(getSafeNumber(session.bytesSent), getSafeNumber(session.bytesReceived))}
                  </motion.span>
                </div>
                <motion.div
                  className="text-gray-500 text-xs"
                  key={getSafeNumber(session.totalBytes) || getSafeNumber(session.bytesSent) + getSafeNumber(session.bytesReceived)}
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {t('sessions.sessionCard.total')} {formatBytes(getSafeNumber(session.totalBytes) || getSafeNumber(session.bytesSent) + getSafeNumber(session.bytesReceived))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Card>
    </motion.div>
  );
};

export default SessionCard;
